import SubPageLayout from "@/app/features/organisation/SubPageLayout";
import OfficeSelector from "@/app/features/profile/OfficeSelector";
import { ProfilePhotoUploader } from "@/components/image-upload/ProfileFileUploader";
import { mediaInfoSchema } from "@/components/image-upload/validation-schema";
import BusyButton from "@/components/raytd/busy-button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { getUser, useAppDispatch, useGetLoggedInUserQuery, useGetOfficesQuery, useUpdateUserProfileMutation } from "@app.raytd.com/store";
import { zodResolver } from "@hookform/resolvers/zod";
import { UserIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import APIError from "store/src/lib/api/apiError";
import { z } from "zod";
import { LinkedInInput } from './LinkedIn';
import { useNav } from './NavContext';
import { PasswordResetPopup } from './PasswordReset';
import Qualifications from './Qualifications';
import { useAuth } from "@/app/_components";

const profileFormSchema = z.object({
    first_name: z.string().min(1, "First name is required"),
    last_name: z.string().min(1, "Last name is required"),
    phone: z.string().optional(),
    email: z.string().email("Invalid email address"),
    //organisation: z.string().optional(),
    position_title: z.string().optional(),
    office_id: z.union([z.number(), z.null()]).optional(),
    linkedin: z.union([z.string().url("Invalid URL"), z.literal(''), z.null()]).optional(),
    photo: mediaInfoSchema.optional().nullable(),
});

type ProfileFormValues = z.infer<typeof profileFormSchema>;

export const ProfileForm = () => {

    const { data: profile, isLoading, isFetching, isError: loadingError } = useGetLoggedInUserQuery({});
    const [updateProfile, { isLoading: isUpdating, isError: isUpdateError, isSuccess: isUpdateSuccessful }] = useUpdateUserProfileMutation();

    const { data: offices, isLoading: officesLoading, isFetching: officesFetching } = useGetOfficesQuery({});

    const [isPhotoUploading, setIsPhotoUploading] = useState(false);

    const handlePhotoUploadStateChange = useCallback((uploading: boolean) => {
        setIsPhotoUploading(uploading);
    }, []);

    const form = useForm<ProfileFormValues>({
        resolver: zodResolver(profileFormSchema),
        defaultValues: {
            first_name: '',
            last_name: '',
            phone: '',
            email: '',
            position_title: '',
            linkedin: '',
            office_id: null,
            photo: null
        },
    });

    const isBusyOverall = isUpdating || isPhotoUploading;
    const canSubmit = form.formState.isDirty && !isBusyOverall;

    const { isDirty } = form.formState;

    const { AlertDialogComponent } = useUnsavedChanges(isDirty, form.reset);
    const { refreshUser } = useAuth();

    const onSubmit = useCallback(async (values: z.infer<typeof profileFormSchema>) => {
        try {
            const data = values;
            const payload = await updateProfile({ ...data }).unwrap();
            refreshUser();

            toast.success("Profile updated");

        } catch (err) {
            console.error(err as APIError);
            toast.error("Failed to update profile");
        }
    }, [updateProfile, form, refreshUser]);


    const { setNavButtons } = useNav();

    useEffect(() => {
        setNavButtons(
            <>
                <PasswordResetPopup />
                <BusyButton
                    onClick={form.handleSubmit(onSubmit)}
                    disabled={!canSubmit}
                    isBusy={isBusyOverall}
                    busyText={isPhotoUploading ? "Uploading..." : "Saving..."}
                >
                    Save
                </BusyButton>
            </>
        );

        // Clean up function to reset buttons when component unmounts
        return () => setNavButtons(null);
    }, [setNavButtons,
        form.handleSubmit,
        onSubmit,
        canSubmit,
        isBusyOverall
    ]);

    useEffect(() => {
        if (profile) {
            console.info('resetting form with profile data:', profile);
            form.reset({
                first_name: profile?.first_name ?? '',
                last_name: profile?.last_name ?? '',
                phone: profile?.phone ?? '',
                email: profile?.email ?? '',
                position_title: profile?.position_title ?? '',
                linkedin: profile?.linkedin ?? '',
                office_id: profile?.office?.id ?? null,
                photo: profile?.photo ?? null
            });
        }
    }, [profile, form.reset]);

    useEffect(() => {
        console.info('errors', form.formState.errors, isDirty);
    }, [form.formState.errors, isDirty]);

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <SubPageLayout title="Profile" showLoading={isLoading}>
            <AlertDialogComponent />
            <Form {...form}  >
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className="grid gap-6 grid-cols-1 md:grid-cols-3 mb-4">
                        <div className="grid gap-6 grid-cols-1 md:grid-cols-2 mb-4 md:col-span-2">
                            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="first_name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>First Name*</FormLabel>
                                            <FormControl>
                                                <Input {...field} placeholder="First Name" required />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="last_name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Last Name*</FormLabel>
                                            <FormControl>
                                                <Input {...field} placeholder="Last Name" required />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <FormField
                                control={form.control}
                                name="phone"
                                render={({ field }) => (
                                    <FormItem className="col-span-2">
                                        <FormLabel>Phone</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="Phone" inputMode="tel" type="tel" />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem className="col-span-2">
                                        <FormLabel>Email</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="Email" readOnly className="bg-muted" />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <FormItem className="col-span-2">
                                <FormLabel>Organisation</FormLabel>
                                <FormControl>
                                    <Input name="company" value={profile?.company?.name} placeholder="Organisation" readOnly className="bg-muted cursor-not-allowed" />
                                </FormControl>
                            </FormItem>
                            <FormField
                                control={form.control}
                                name="position_title"
                                render={({ field }) => (
                                    <FormItem className="col-span-2">
                                        <FormLabel>Position Title</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="Position Title" />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div>
                            <FormField
                                control={form.control}
                                name="photo"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Photo</FormLabel>
                                        <FormControl>
                                            <ProfilePhotoUploader
                                                formId={'profile-photo'}
                                                rounded={true}
                                                size="2xl"
                                                //@ts-expect-error
                                                value={field.value}
                                                onChange={field.onChange}
                                                fallbackIcon={UserIcon}
                                                onUploadStateChange={handlePhotoUploadStateChange}
                                                disabled={isBusyOverall}
                                            />
                                        </FormControl>


                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                    </div>


                    <div className="flex flex-col gap-4">
                        <Qualifications />

                        <div>

                            <h3 className="text-sm font-medium mb-2">Office</h3>
                            <FormField
                                control={form.control}
                                name="office_id"
                                render={({ field }) => (
                                    <FormControl>

                                        <OfficeSelector
                                            office={field.value}
                                            editable={true}
                                            offices={offices}
                                            isLoading={officesLoading || officesFetching}
                                            onSelect={(office) => {
                                                console.info('selected office', office);
                                                field.onChange(office.id);
                                            }}

                                        />
                                    </FormControl>
                                )}
                            />


                        </div>

                        <div>

                            <FormField
                                control={form.control}
                                name="linkedin"
                                render={({ field }) => (
                                    <LinkedInInput
                                        label="LinkedIn Profile"
                                        description="Your LinkedIn profile URL (optional)"
                                        placeholder="username"
                                        {...field}
                                        value={field.value || ""}
                                        onChange={(value) => field.onChange(value || null)}
                                    />
                                )}
                            />
                        </div>

                    </div>
                </form>
            </Form>
        </SubPageLayout>
    );
};
